import { useCallback, useRef, useState } from 'react'
import { saveBlob } from './saveBlob'

export type DownloadResult = { status: 'saved' } | { status: 'failed'; error: unknown } | { status: 'ignored' }

/**
 * The mechanics every file export shares: one download at a time, a `busy` flag for `aria-busy`, and the blob
 * saved through `saveBlob`. The caller supplies what to fetch and decides what a failure means (toast, analytics);
 * `produce` is read at call time, so a retry that calls `run` again with fresh inputs exports the current view.
 */
export function useBlobDownload(): { busy: boolean; run: (produce: () => Promise<{ blob: Blob; filename: string }>) => Promise<DownloadResult> } {
  const [busy, setBusy] = useState(false)
  const running = useRef(false)

  const run = useCallback(async (produce: () => Promise<{ blob: Blob; filename: string }>): Promise<DownloadResult> => {
    if (running.current) return { status: 'ignored' }
    running.current = true
    setBusy(true)
    try {
      const file = await produce()
      saveBlob(file.blob, file.filename)
      return { status: 'saved' }
    } catch (error) {
      return { status: 'failed', error }
    } finally {
      running.current = false
      setBusy(false)
    }
  }, [])

  return { busy, run }
}
